import { BuyerAudience, ClinicProduct, VirtualConfig } from '../types';

/**
 * Backend `ProductBuyerDto` (swagger schema):
 *   id, name, sku, productType, category, description,
 *   stockStatus, availableStock, moqEnabled, moq,
 *   clinicSalePrice, gstRate, freightFee, handlingFee,
 *   supplierPublicBrandName, supplierPublicSlug,
 *   virtualConfig, publicShareUrl, imageUrls, unit, specification
 *
 * Frontend `ClinicProduct` uses `salePrice`, `spec`, nested `supplier`, etc.
 * This mapper bridges the two until backend renames `clinicSalePrice` (P2 backlog).
 */
export type ProductBuyerDtoBackend = {
  id: string;
  /** Legacy single name; kept for back-compat until backend always sends nameEn. */
  name?: string;
  nameEn?: string;
  nameZh?: string;
  sku?: string;
  productType: 'PHYSICAL' | 'VIRTUAL';
  category: ClinicProduct['category'];
  description?: string;
  stockStatus: ClinicProduct['stockStatus'];
  availableStock?: number;
  moqEnabled?: boolean;
  moq?: number;
  clinicSalePrice?: number | null;
  businessPrice?: number | null;
  individualPrice?: number | null;
  gstRate: number;
  freightFee?: number;
  handlingFee?: number;
  supplierPublicBrandName?: string;
  supplierPublicSlug?: string;
  supplierPublicLogo?: string;
  // Backend may leave reservedSlots / soldSlots / remainingSlots as null when
  // a virtual product has just been listed and nothing has been booked yet.
  // The frontend `VirtualConfig` type expects numbers, so we normalize below.
  virtualConfig?: {
    format: VirtualConfig['format'];
    capacity?: number | null;
    reservedSlots?: number | null;
    soldSlots?: number | null;
    remainingSlots?: number | null;
    autoDelistAt?: string | null;
    courseSchedule?: {
      startAt?: string | null;
      endAt?: string | null;
      location?: string | null;
      onlineLink?: string | null;
    } | null;
  } | null;
  publicShareUrl?: string;
  imageUrls?: string[];
  unit?: string;
  specification?: string;
  buyerAudience?: BuyerAudience | null;
};

const normalizeVirtualConfig = (
  raw: ProductBuyerDtoBackend['virtualConfig'],
): VirtualConfig | undefined => {
  if (!raw) return undefined;
  return {
    format: raw.format,
    // Preserve null — backend uses null to mean "unlimited capacity".
    capacity: raw.capacity ?? null,
    reservedSlots: raw.reservedSlots ?? 0,
    soldSlots: raw.soldSlots ?? 0,
    autoDelistAt: raw.autoDelistAt ?? undefined,
    // Backend nests course schedule; flatten into FE's flat fields.
    courseStartAt: raw.courseSchedule?.startAt ?? undefined,
    courseEndAt: raw.courseSchedule?.endAt ?? undefined,
  };
};

// Derive stock pose for VIRTUAL products from virtualConfig. Backend leaves
// `stockStatus` / `availableStock` null for virtual SKUs because the concept
// is slot-based, not warehouse-based; without normalization the rest of the
// app (StockPill, AddToCartButton, isOutOfStock helper, cart row) treats
// every virtual product as "out of stock".
const deriveVirtualStock = (
  vc: VirtualConfig | undefined,
): { stockStatus: ClinicProduct['stockStatus']; availableStock: number } => {
  if (!vc) return { stockStatus: 'OUT_OF_STOCK', availableStock: 0 };
  const delisted = !!vc.autoDelistAt && Date.parse(vc.autoDelistAt) <= Date.now();
  if (delisted) return { stockStatus: 'OUT_OF_STOCK', availableStock: 0 };
  // Unlimited capacity: no slot cap, always in-stock.
  if (vc.capacity === null) {
    return { stockStatus: 'IN_STOCK', availableStock: 999999 };
  }
  const remaining = Math.max(0, vc.capacity - vc.reservedSlots - vc.soldSlots);
  if (remaining <= 0) return { stockStatus: 'OUT_OF_STOCK', availableStock: 0 };
  return { stockStatus: 'IN_STOCK', availableStock: remaining };
};

export const mapProductBuyerDto = (b: ProductBuyerDtoBackend): ClinicProduct => {
  const virtualConfig = normalizeVirtualConfig(b.virtualConfig);
  const isVirtual = b.productType === 'VIRTUAL';
  const virtualStock = isVirtual ? deriveVirtualStock(virtualConfig) : null;
  return {
  id: b.id,
  sku: b.sku,
  nameEn: b.nameEn ?? b.name ?? '',
  nameZh: b.nameZh ?? undefined,
  productType: b.productType,
  imageUrl: b.imageUrls?.[0],
  imageUrls: b.imageUrls,
  brand: b.supplierPublicBrandName,
  category: b.category,
  unit: b.unit ?? '',
  spec: b.specification,
  // `clinicSalePrice` is the V1 single price; V2.2 backend may send only
  // businessPrice/individualPrice. Fall back so legacy reads (cart line stub,
  // any UI that still reads .salePrice) get a usable number instead of NaN.
  salePrice:
    typeof b.clinicSalePrice === 'number' && !Number.isNaN(b.clinicSalePrice)
      ? b.clinicSalePrice
      : b.businessPrice ?? b.individualPrice ?? 0,
  businessPrice: b.businessPrice ?? undefined,
  individualPrice: b.individualPrice ?? undefined,
  gstRate: b.gstRate,
  stockStatus: virtualStock?.stockStatus ?? b.stockStatus,
  availableStock: virtualStock?.availableStock ?? b.availableStock ?? 0,
  moq: b.moqEnabled ? b.moq ?? undefined : undefined,
  virtualConfig,
  supplier:
    b.supplierPublicSlug || b.supplierPublicBrandName
      ? {
          // Backend doesn't expose supplierId in this DTO — use slug as a stable key.
          supplierId: b.supplierPublicSlug ?? '',
          publicSlug: b.supplierPublicSlug ?? '',
          publicBrandName: b.supplierPublicBrandName ?? '',
          publicLogo: b.supplierPublicLogo ?? undefined,
        }
      : undefined,
  publicShareUrl: b.publicShareUrl,
  buyerAudience: b.buyerAudience ?? 'BOTH',
  };
};
